import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, RadioGroup, FormControlLabel, Radio, Checkbox, FormControl, FormLabel, TextField, Grid, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Switch, FormGroup, Divider } from '@mui/material';
import { doc, setDoc, getDoc, writeBatch, deleteDoc, updateDoc, addDoc, collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateForFirestore } from '../../utils/dateUtils';
import ErrorHandler from '../../utils/errorHandler';
import { canEditOrRegisterShift, getRestrictionMessage } from '../../utils/weekRestrictions';
import { pickScheduleDocId, checkShiftConflicts, getShiftConflictMessage, getShiftName, formatScheduleDate } from '../../utils/scheduleShiftUtils';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import RestoreIcon from '@mui/icons-material/Restore';
import RepeatIcon from '@mui/icons-material/Repeat';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

// Helper function để lấy giờ mặc định, giúp code gọn hơn
const getDefaultTimes = (shift, settings) => {
  if (!shift || !settings || Object.keys(settings).length === 0) {
    return { start: '', end: '' };
  }
  switch (shift) {
    case 'morning': return { start: settings.morning_start, end: settings.morning_end };
    case 'lunch': return { start: settings.lunch_start, end: settings.lunch_end };
    case 'afternoon': return { start: settings.afternoon_start, end: settings.afternoon_end };
    case 'evening': return { start: settings.evening_start, end: settings.evening_end };
    case 'full_day': return { start: settings.morning_start, end: settings.afternoon_end };
    default: return { start: '', end: '' };
  }
};

// Helper function để tạo danh sách ngày cho chu kỳ lặp (chỉ hỗ trợ lặp theo tuần)
const generateRecurringDates = (startDate, recurringDays, recurringShifts, endDate) => {
  const dates = [];
  const start = new Date(startDate);
  // Fix timezone issue: append time to ensure end date is inclusive
  const end = endDate
    ? new Date(endDate + 'T23:59:59')
    : new Date(start.getFullYear() + 1, start.getMonth(), start.getDate());

  // Reset time to midnight for consistent comparison
  const current = new Date(start);
  current.setHours(0, 0, 0, 0);

  // Lặp theo tuần
  for (let d = current; d <= end; d.setDate(d.getDate() + 1)) {
    if (recurringDays.includes(d.getDay())) {
      dates.push(new Date(d));
    }
  }

  return dates;
};

// Helper function để tạo registration data cho chu kỳ lặp
const createRecurringRegistrationData = (baseData, targetDate, targetShift, customStartTime = null, customEndTime = null, useCustomTime = false, recurringId = null) => {
  let finalStartTime, finalEndTime;

  if (useCustomTime && customStartTime && customEndTime) {
    // Sử dụng giờ tùy chỉnh của người dùng
    finalStartTime = customStartTime;
    finalEndTime = customEndTime;
  } else {
    // Sử dụng giờ mặc định
    const defaultTimes = getDefaultTimes(targetShift, baseData.defaultSettings);
    finalStartTime = defaultTimes.start;
    finalEndTime = defaultTimes.end;
  }

  // Tạo recurringId nếu chưa có (cho ca đầu tiên)
  const finalRecurringId = recurringId || `${baseData.userId}_${formatDateForFirestore(new Date())}_${Math.random().toString(36).substr(2, 9)}`;

  return {
    ...baseData,
    date: formatDateForFirestore(targetDate),
    shiftType: targetShift,
    startTime: finalStartTime,
    endTime: finalEndTime,
    isThroughLunch: targetShift === 'full_day' ? baseData.isThroughLunch : false,
    createdAt: new Date(),
    updatedAt: new Date(),
    isRecurring: true,
    recurringId: finalRecurringId
  };
};

export default function RegisterModal({ open, handleClose, date, initialShift, onRegisterSuccess, existingRegistration, regWindow, onAdminEdit, isAdminView }) {
  const { currentUser } = useAuth();
  const [shiftType, setShiftType] = useState('');
  const [isThroughLunch, setIsThroughLunch] = useState(false);
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [defaultSettings, setDefaultSettings] = useState({});
  const [customStartTime, setCustomStartTime] = useState('');
  const [customEndTime, setCustomEndTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(null); // Nâng cấp: null | 'time' | 'shift'
  const [cancelShiftDialogOpen, setCancelShiftDialogOpen] = useState(false);
  const [cancelShiftLoading, setCancelShiftLoading] = useState(false);
  const [emergencyLeaveDialogOpen, setEmergencyLeaveDialogOpen] = useState(false);
  const [emergencyLeaveLoading, setEmergencyLeaveLoading] = useState(false);
  const [emergencyLeaveReason, setEmergencyLeaveReason] = useState('');
  const [note, setNote] = useState(''); // Thêm state cho ghi chú
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  // States cho chức năng xóa ca của nhân viên
  const [deleteShiftDialogOpen, setDeleteShiftDialogOpen] = useState(false);
  const [deleteShiftLoading, setDeleteShiftLoading] = useState(false);

  // States cho chức năng xóa chu kỳ lặp
  const [deleteRecurringDialogOpen, setDeleteRecurringDialogOpen] = useState(false);
  const [deleteRecurringLoading, setDeleteRecurringLoading] = useState(false);
  const [recurringInfo, setRecurringInfo] = useState(null);
  const [deleteOption, setDeleteOption] = useState('single'); // 'single' hoặc 'recurring'

  // States cho chức năng chu kỳ lặp
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringDays, setRecurringDays] = useState([]); // [0,1,2,3,4,5,6] cho thứ trong tuần
  const [recurringShifts, setRecurringShifts] = useState([]); // ['morning', 'afternoon', 'lunch', 'evening']
  const [recurringEndDate, setRecurringEndDate] = useState('');

  // Lấy cài đặt mặc định từ Firestore khi modal được mở
  useEffect(() => {
    const fetchSettings = async () => {
      const settingsDocRef = doc(db, 'settings', 'work_hours');
      const docSnap = await getDoc(settingsDocRef);
      if (docSnap.exists()) {
        setDefaultSettings(docSnap.data());
      }
    };
    if (open) {
      setError('');
      setEditMode(null); // Reset chế độ chỉnh sửa mỗi khi mở modal
      fetchSettings();
    }
  }, [open]);

  // Thiết lập trạng thái ban đầu khi modal mở hoặc có dữ liệu mới
  useEffect(() => {
    if (!open || Object.keys(defaultSettings).length === 0) return;

    if (existingRegistration) {
      // Chế độ chỉnh sửa
      setShiftType(existingRegistration.shiftType);
      setIsThroughLunch(existingRegistration.isThroughLunch || false);
      setCustomStartTime(existingRegistration.startTime);
      setCustomEndTime(existingRegistration.endTime);
      setNote(existingRegistration.note || ''); // Khởi tạo ghi chú từ dữ liệu hiện có
      const defaultTimes = getDefaultTimes(existingRegistration.shiftType, defaultSettings);
      const isCustom = existingRegistration.startTime !== defaultTimes.start || existingRegistration.endTime !== defaultTimes.end;
      setUseCustomTime(isCustom);
    } else {
      // Chế độ đăng ký mới
      setShiftType(initialShift);
      setIsThroughLunch(false);
      setUseCustomTime(false);
      setNote(''); // Reset ghi chú cho đăng ký mới
      const defaultTimes = getDefaultTimes(initialShift, defaultSettings);
      setCustomStartTime(defaultTimes.start);
      setCustomEndTime(defaultTimes.end);
    }
  }, [open, existingRegistration, defaultSettings, initialShift]);

  // Load thông tin chu kỳ lặp nếu ca hiện tại thuộc chu kỳ lặp
  useEffect(() => {
    const loadRecurringInfo = async () => {
      if (!existingRegistration || !existingRegistration.isRecurring || !existingRegistration.recurringId) {
        setRecurringInfo(null);
        return;
      }

      try {
        // Tìm tất cả ca thuộc cùng chu kỳ lặp
        const recurringQuery = query(
          collection(db, 'schedules'),
          where('recurringId', '==', existingRegistration.recurringId),
          where('userId', '==', existingRegistration.userId),
          orderBy('date', 'asc')
        );

        const querySnapshot = await getDocs(recurringQuery);
        const recurringShifts = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        if (recurringShifts.length > 0) {
          setRecurringInfo({
            recurringId: existingRegistration.recurringId,
            totalShifts: recurringShifts.length,
            shifts: recurringShifts,
            firstDate: recurringShifts[0].date,
            lastDate: recurringShifts[recurringShifts.length - 1].date,
            shiftType: existingRegistration.shiftType
          });

          // Tự động chọn "single" nếu chỉ có 1 ca
          if (recurringShifts.length === 1) {
            setDeleteOption('single');
          }
        }
      } catch (error) {
        console.error('Lỗi load thông tin chu kỳ lặp:', error);
        setRecurringInfo(null);
      }
    };

    if (open && existingRegistration) {
      loadRecurringInfo();
    }
  }, [open, existingRegistration]);

  const handleShiftTypeChange = (newShiftType) => {
    setShiftType(newShiftType);
    if (Object.keys(defaultSettings).length > 0) {
      const defaultTimes = getDefaultTimes(newShiftType, defaultSettings);
      setCustomStartTime(defaultTimes.start);
      setCustomEndTime(defaultTimes.end);
      // Khi đổi ca, luôn reset về giờ mặc định và không hiển thị ô tùy chỉnh
      setUseCustomTime(false);
    }
  };

  const handleRegister = async () => {
    setLoading(true);
    setError('');
    const targetUser = existingRegistration
      ? { uid: existingRegistration.userId, displayName: existingRegistration.userName, photoURL: existingRegistration.userPhoto || "" }
      : { uid: currentUser.uid, displayName: currentUser.displayName, photoURL: currentUser.photoURL || "" };

    if (!targetUser || (!date && !existingRegistration)) {
      setError("Thiếu thông tin người dùng hoặc ngày đăng ký.");
      setLoading(false);
      return;
    };

    // Định nghĩa regDate ở ngoài try block để có thể sử dụng trong catch
    const regDate = existingRegistration ? new Date(existingRegistration.date + 'T00:00:00') : date;

    try {
      // Kiểm tra quyền chỉnh sửa/đăng ký TRƯỚC mọi thao tác ghi/xóa
      const editResult = canEditOrRegisterShift(regDate, regWindow, isAdminView);
      if (!editResult.canEdit) {
        setError(getRestrictionMessage(editResult.reason));
        setLoading(false);
        return;
      }

      const isRecurringNew = isRecurring && !existingRegistration;

      // Validation cho chu kỳ lặp
      if (isRecurringNew) {
        if (recurringDays.length === 0) {
          setError("Vui lòng chọn ít nhất một ngày trong tuần cho chu kỳ lặp hàng tuần.");
          setLoading(false);
          return;
        }
        if (recurringEndDate && new Date(recurringEndDate) <= regDate) {
          setError("Ngày kết thúc phải sau ngày bắt đầu.");
          setLoading(false);
          return;
        }
      }

      // OVERLAP VALIDATION BLOCK
      const datesToCheck = isRecurringNew
        ? generateRecurringDates(regDate, recurringDays, recurringShifts, recurringEndDate)
        : [regDate];

      // Lịch hiện có của nhân viên trong khoảng ngày (dùng để kiểm tra trùng và chọn mã document)
      let userSchedules = [];
      // Các ca Sáng/Chiều bị ca Cả ngày bao trùm: xóa CÙNG batch với thao tác ghi bên dưới
      const autoDeletes = [];

      if (datesToCheck.length > 0) {
        const startDateStr = formatDateForFirestore(datesToCheck[0]);
        const endDateStr = formatDateForFirestore(datesToCheck[datesToCheck.length - 1]);
        const qCheck = query(collection(db, 'schedules'),
          where('userId', '==', targetUser.uid),
          where('date', '>=', startDateStr),
          where('date', '<=', endDateStr)
        );
        const snap = await getDocs(qCheck);
        userSchedules = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Ca hiện tại đang sửa (để không tự check trùng với chính nó)
        const editingId = (existingRegistration && editMode !== 'additional_shift') ? existingRegistration.id : null;

        const shiftsToRegister = (isRecurringNew && recurringShifts.length > 0)
          ? recurringShifts
          : [shiftType];

        // Chặn trùng loại ca khi đăng ký mới / đổi sang ca khác. Sửa giờ, sửa ghi chú giữ nguyên loại ca;
        // đăng ký chu kỳ lặp ghi đè ca cùng loại đã có như trước.
        const checkDuplicate = !isRecurringNew && (!editingId || shiftType !== existingRegistration.shiftType);

        for (const d of datesToCheck) {
          const dStr = formatDateForFirestore(d);
          const dayOthers = userSchedules.filter(s => s.date === dStr && s.id !== editingId);
          const { conflict, deleteIds } = checkShiftConflicts({ daySchedules: dayOthers, shiftsToRegister, checkDuplicate });
          if (conflict) {
            setError(getShiftConflictMessage(conflict, dStr));
            setLoading(false);
            return;
          }
          autoDeletes.push(...deleteIds);
        }
      }
      // END OVERLAP VALIDATION BLOCK

      // Nếu là chu kỳ lặp, xử lý đặc biệt
      if (isRecurringNew) {
        await handleRecurringRegistration(targetUser, regDate, userSchedules, autoDeletes);
      } else {
        // Xử lý đăng ký thông thường
        await handleSingleRegistration(targetUser, regDate, userSchedules, autoDeletes);
      }

      onRegisterSuccess();
      handleClose();
    } catch (error) {
      const errorMessage = ErrorHandler.handleError(error, 'đăng ký/cập nhật lịch');
      ErrorHandler.logError(error, 'RegisterModal.handleRegister', {
        userId: targetUser?.uid,
        date: regDate?.toISOString(),
        shiftType,
        isRecurring
      });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Hàm xử lý đăng ký đơn lẻ
  const handleSingleRegistration = async (targetUser, regDate, userSchedules = [], autoDeletes = []) => {
    const dateStr = formatDateForFirestore(regDate);
    const isEditingExisting = !!existingRegistration && editMode !== 'additional_shift';

    // Sửa ca có sẵn (đổi giờ / đổi ca / ghi chú): ghi vào đúng document đang sửa. Sau khi đổi ca,
    // mã document có thể không khớp loại ca, nên không được suy mã từ shiftType.
    // Đăng ký mới / thêm ca bổ sung: chọn mã không đè lên ca khác của nhân viên trong ngày.
    const registrationId = isEditingExisting
      ? existingRegistration.id
      : pickScheduleDocId(dateStr, targetUser.uid, shiftType, userSchedules.filter(s => !autoDeletes.includes(s.id)));
    const registrationDocRef = doc(db, 'schedules', registrationId);

    let finalStartTime = customStartTime;
    let finalEndTime = customEndTime;

    if ((editMode === 'shift' || editMode === 'additional_shift') && !useCustomTime) {
      const defaultTimes = getDefaultTimes(shiftType, defaultSettings);
      finalStartTime = defaultTimes.start;
      finalEndTime = defaultTimes.end;
    }

    const registrationData = {
      userId: targetUser.uid,
      userName: targetUser.displayName,
      userPhoto: targetUser.photoURL || "",
      date: dateStr,
      shiftType,
      isThroughLunch: shiftType === 'full_day' ? isThroughLunch : false,
      startTime: finalStartTime,
      endTime: finalEndTime,
      status: existingRegistration?.status || 'working',
      department: currentUser.department || '',
      position: currentUser.position || '',
      note: note.trim() || null,
      createdAt: existingRegistration?.createdAt?.toDate() || new Date(),
      updatedAt: new Date(),
      lastUpdatedBy: { uid: currentUser.uid, name: currentUser.displayName }
    };

    const batch = writeBatch(db);

    // Các ca Sáng/Chiều bị ca Cả ngày bao trùm: xóa trong cùng batch để không mất ca nếu ghi thất bại
    autoDeletes
      .filter(id => id !== registrationDocRef.id)
      .forEach(id => batch.delete(doc(db, 'schedules', id)));

    batch.set(registrationDocRef, registrationData, { merge: true });
    await batch.commit();
    console.log('✅ Đã lưu ca làm việc thành công:', registrationData);

    // Tạo thông báo nếu admin chỉnh sửa ca làm việc của nhân viên
    if (isAdminView && existingRegistration && onAdminEdit) {
      const oldData = {
        shiftType: existingRegistration.shiftType,
        startTime: existingRegistration.startTime,
        endTime: existingRegistration.endTime,
        isThroughLunch: !!existingRegistration.isThroughLunch
      };
      const newData = {
        shiftType: registrationData.shiftType,
        startTime: registrationData.startTime,
        endTime: registrationData.endTime,
        isThroughLunch: registrationData.isThroughLunch
      };

      if (JSON.stringify(oldData) !== JSON.stringify(newData)) {
        await onAdminEdit(existingRegistration, oldData, newData);
      }
    }
  };

  // Hàm xử lý đăng ký chu kỳ lặp
  const handleRecurringRegistration = async (targetUser, startDate, userSchedules = [], autoDeletes = []) => {
    const baseData = {
      userId: targetUser.uid,
      userName: targetUser.displayName,
      userPhoto: targetUser.photoURL || "",
      status: 'working',
      department: currentUser.department || '',
      position: currentUser.position || '',
      note: note.trim() || null,
      lastUpdatedBy: { uid: currentUser.uid, name: currentUser.displayName },
      defaultSettings
    };

    // Tạo recurringId duy nhất cho toàn bộ chu kỳ lặp
    const recurringId = `${baseData.userId}_${formatDateForFirestore(new Date())}_${Math.random().toString(36).substr(2, 9)}`;

    // Tạo danh sách ngày cho chu kỳ lặp
    const dates = generateRecurringDates(startDate, recurringDays, recurringShifts, recurringEndDate);

    // Tạo danh sách ca làm việc
    const shiftsToCreate = [];
    if (recurringShifts.length > 0) {
      // Nếu chọn nhiều ca trong ngày
      for (const targetDate of dates) {
        for (const targetShift of recurringShifts) {
          const registrationData = createRecurringRegistrationData(
            baseData,
            targetDate,
            targetShift,
            customStartTime,
            customEndTime,
            useCustomTime,
            recurringId
          );
          shiftsToCreate.push(registrationData);
        }
      }
    } else {
      // Nếu chỉ chọn 1 ca (ca hiện tại)
      for (const targetDate of dates) {
        const registrationData = createRecurringRegistrationData(
          baseData,
          targetDate,
          shiftType,
          customStartTime,
          customEndTime,
          useCustomTime,
          recurringId
        );
        shiftsToCreate.push(registrationData);
      }
    }

    // Lưu tất cả ca làm việc vào database
    const batch = writeBatch(db);
    // Các ca Sáng/Chiều bị ca Cả ngày bao trùm: xóa trong cùng batch với thao tác ghi
    autoDeletes.forEach(id => batch.delete(doc(db, 'schedules', id)));
    const remainingSchedules = userSchedules.filter(s => !autoDeletes.includes(s.id));
    for (const registrationData of shiftsToCreate) {
      // Chọn mã không đè lên ca khác loại đang nằm ở mã chuẩn (do đổi ca trước đó)
      const registrationId = pickScheduleDocId(registrationData.date, targetUser.uid, registrationData.shiftType, remainingSchedules);
      const registrationDocRef = doc(db, 'schedules', registrationId);
      batch.set(registrationDocRef, registrationData, { merge: true });
    }

    await batch.commit();
    console.log(`✅ Đã tạo ${shiftsToCreate.length} ca làm việc chu kỳ lặp`);
  };


  // Hàm hủy ca làm việc (chỉ dành cho admin)
  const handleCancelShift = async () => {
    setCancelShiftLoading(true);
    setError('');

    try {
      if (existingRegistration) {
        // Chuyển ca sang trạng thái "cancelled" thay vì xóa
        await updateDoc(doc(db, 'schedules', existingRegistration.id), {
          status: 'cancelled',
          cancelledBy: { uid: currentUser.uid, name: currentUser.displayName },
          cancelledAt: new Date(),
          updatedAt: new Date()
        });

        // Tạo thông báo cho nhân viên
        if (isAdminView && onAdminEdit) {
          const notificationData = {
            userId: existingRegistration.userId,
            type: 'admin_cancel_shift',
            title: 'Admin đã hủy ca làm việc của bạn',
            message: `Admin ${currentUser.displayName} đã hủy ${getShiftName(existingRegistration.shiftType)} (${existingRegistration.startTime}-${existingRegistration.endTime}) của bạn ngày ${formatScheduleDate(existingRegistration.date)}`,
            date: existingRegistration.date,
            createdAt: new Date(),
            // Chuông thông báo sắp xếp theo `timestamp`: thiếu trường này thì thông báo không hiển thị
            timestamp: new Date(),
            isRead: false
          };

          // Chỉ thêm userEmail nếu có
          if (existingRegistration.userEmail) {
            notificationData.userEmail = existingRegistration.userEmail;
          }

          await addDoc(collection(db, 'notifications'), notificationData);
        }

        onRegisterSuccess();
        handleClose();
      }
    } catch (error) {
      const errorMessage = ErrorHandler.handleError(error, 'hủy ca làm việc');
      ErrorHandler.logError(error, 'RegisterModal.handleCancelShift', {
        registrationId: existingRegistration?.id
      });
      setError(errorMessage);
    } finally {
      setCancelShiftLoading(false);
      setCancelShiftDialogOpen(false);
    }
  };

  // Hàm khôi phục ca đã hủy (chỉ dành cho admin)
  const handleRestoreShift = async () => {
    setRestoreLoading(true);
    setError('');

    try {
      if (existingRegistration) {
        // Khôi phục ca về trạng thái "working"
        await updateDoc(doc(db, 'schedules', existingRegistration.id), {
          status: 'working',
          restoredBy: { uid: currentUser.uid, name: currentUser.displayName },
          restoredAt: new Date(),
          updatedAt: new Date()
        });

        // Tạo thông báo cho nhân viên
        if (isAdminView && onAdminEdit) {
          const notificationData = {
            userId: existingRegistration.userId,
            type: 'admin_restore_shift',
            title: 'Admin đã khôi phục ca làm việc của bạn',
            message: `Admin ${currentUser.displayName} đã khôi phục ${getShiftName(existingRegistration.shiftType)} (${existingRegistration.startTime}-${existingRegistration.endTime}) của bạn ngày ${formatScheduleDate(existingRegistration.date)}`,
            date: existingRegistration.date,
            createdAt: new Date(),
            timestamp: new Date(),
            isRead: false
          };

          // Chỉ thêm userEmail nếu có
          if (existingRegistration.userEmail) {
            notificationData.userEmail = existingRegistration.userEmail;
          }

          await addDoc(collection(db, 'notifications'), notificationData);
        }

        onRegisterSuccess();
        handleClose();
      }
    } catch (error) {
      const errorMessage = ErrorHandler.handleError(error, 'khôi phục ca làm việc');
      ErrorHandler.logError(error, 'RegisterModal.handleRestoreShift', {
        registrationId: existingRegistration?.id
      });
      setError(errorMessage);
    } finally {
      setRestoreLoading(false);
      setRestoreDialogOpen(false);
    }
  };

  // Hàm xóa ca làm việc của nhân viên (xóa hẳn khỏi database)
  const handleDeleteShift = async () => {
    if (!existingRegistration) return;

    setDeleteRecurringLoading(true);
    setError('');

    try {
      // Xóa hẳn ca làm việc khỏi database
      await deleteDoc(doc(db, 'schedules', existingRegistration.id));

      console.log('✅ Đã xóa ca làm việc thành công:', existingRegistration.id);

      onRegisterSuccess();
      handleClose();
    } catch (error) {
      const errorMessage = ErrorHandler.handleError(error, 'xóa ca làm việc');
      ErrorHandler.logError(error, 'RegisterModal.handleDeleteShift', {
        registrationId: existingRegistration?.id
      });
      setError(errorMessage);
    } finally {
      setDeleteRecurringLoading(false);
      setDeleteRecurringDialogOpen(false);
      setDeleteOption('single');
    }
  };

  // Hàm xóa chu kỳ lặp
  const handleDeleteRecurring = async () => {
    if (!recurringInfo) return;

    setDeleteRecurringLoading(true);
    setError('');

    try {
      // Xóa tất cả ca thuộc chu kỳ lặp
      const batch = writeBatch(db);
      for (const shift of recurringInfo.shifts) {
        const shiftDocRef = doc(db, 'schedules', shift.id);
        batch.delete(shiftDocRef);
      }

      await batch.commit();

      console.log(`✅ Đã xóa chu kỳ lặp thành công: ${recurringInfo.totalShifts} ca`);

      onRegisterSuccess();
      handleClose();
    } catch (error) {
      const errorMessage = ErrorHandler.handleError(error, 'xóa chu kỳ lặp');
      ErrorHandler.logError(error, 'RegisterModal.handleDeleteRecurring', {
        recurringId: recurringInfo?.recurringId,
        totalShifts: recurringInfo?.totalShifts
      });
      setError(errorMessage);
    } finally {
      setDeleteRecurringLoading(false);
      setDeleteRecurringDialogOpen(false);
      setDeleteOption('single');
    }
  };

  // Hàm nghỉ đột xuất (chỉ dành cho admin)
  const handleEmergencyLeave = async () => {
    if (!emergencyLeaveReason.trim()) {
      setError("Vui lòng nhập lý do nghỉ đột xuất.");
      return;
    }

    setEmergencyLeaveLoading(true);
    setError('');

    try {
      if (existingRegistration) {
        // Cập nhật trạng thái thành nghỉ đột xuất
        await updateDoc(doc(db, 'schedules', existingRegistration.id), {
          status: 'emergency_leave',
          emergencyLeaveReason: emergencyLeaveReason.trim(),
          emergencyLeaveBy: { uid: currentUser.uid, name: currentUser.displayName },
          emergencyLeaveAt: new Date()
        });

        // Tạo thông báo cho nhân viên
        if (isAdminView && onAdminEdit) {
          const notificationData = {
            userId: existingRegistration.userId,
            type: 'admin_emergency_leave',
            title: 'Admin đã đánh dấu bạn nghỉ đột xuất',
            message: `Admin ${currentUser.displayName} đã đánh dấu bạn nghỉ đột xuất cho ${getShiftName(existingRegistration.shiftType)} (${existingRegistration.startTime}-${existingRegistration.endTime}) ngày ${formatScheduleDate(existingRegistration.date)}. Lý do: ${emergencyLeaveReason.trim()}`,
            date: existingRegistration.date,
            createdAt: new Date(),
            timestamp: new Date(),
            isRead: false
          };

          // Chỉ thêm userEmail nếu có
          if (existingRegistration.userEmail) {
            notificationData.userEmail = existingRegistration.userEmail;
          }

          await addDoc(collection(db, 'notifications'), notificationData);
        }

        onRegisterSuccess();
        handleClose();
      }
    } catch (error) {
      const errorMessage = ErrorHandler.handleError(error, 'đánh dấu nghỉ đột xuất');
      ErrorHandler.logError(error, 'RegisterModal.handleEmergencyLeave', {
        registrationId: existingRegistration?.id,
        reason: emergencyLeaveReason
      });
      setError(errorMessage);
    } finally {
      setEmergencyLeaveLoading(false);
      setEmergencyLeaveDialogOpen(false);
      setEmergencyLeaveReason('');
    }
  };

  const allShiftOptions = [
    { value: 'morning', label: '🌅 Ca Sáng' },
    { value: 'lunch', label: '🍽️ Ca Trưa' },
    { value: 'afternoon', label: '🌞 Ca Chiều' },
    { value: 'evening', label: '🌙 Ca Tối' },
    { value: 'full_day', label: '☀️ Cả Ngày' }
  ];

  const newRegistrationOptions = {
    morning: [{ value: 'morning', label: 'Ca Sáng' }, { value: 'full_day', label: 'Cả Ngày' }],
    lunch: [{ value: 'lunch', label: 'Ca Trưa' }],
    afternoon: [{ value: 'afternoon', label: 'Ca Chiều' }, { value: 'full_day', label: 'Cả Ngày' }],
    evening: [{ value: 'evening', label: 'Ca Tối' }],
  };

  const renderEditContent = () => {
    if (!editMode) {
      return (
        <>
          {/* Hiển thị thông tin ca làm việc hiện tại */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Thông tin ca làm việc hiện tại:
            </Typography>
            <Typography variant="body2">
              <strong>Ca:</strong> {allShiftOptions.find(opt => opt.value === existingRegistration.shiftType)?.label || existingRegistration.shiftType}
              {existingRegistration.status === 'cancelled' && (
                <Chip
                  label="Đã hủy"
                  size="small"
                  color="warning"
                  sx={{ ml: 1 }}
                />
              )}
              {existingRegistration.status === 'emergency_leave' && (
                <Chip
                  label="Nghỉ đột xuất"
                  size="small"
                  color="error"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            <Typography variant="body2">
              <strong>Thời gian:</strong> {existingRegistration.startTime} - {existingRegistration.endTime}
            </Typography>
            {existingRegistration.note && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Ghi chú:</strong> {existingRegistration.note}
              </Typography>
            )}
          </Box>

          {/* Hiển thị thông tin chu kỳ lặp nếu có */}
          {recurringInfo && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <RepeatIcon color="info" />
                <Typography variant="subtitle2" color="info.main">
                  Chu kỳ lặp
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Tổng số ca:</strong> {recurringInfo.totalShifts} ca
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Thời gian:</strong> {recurringInfo.firstDate} - {recurringInfo.lastDate}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Loại ca:</strong> {allShiftOptions.find(opt => opt.value === recurringInfo.shiftType)?.label || recurringInfo.shiftType}
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Ca này thuộc chu kỳ lặp. Bạn có thể xóa toàn bộ chu kỳ lặp hoặc chỉnh sửa ca riêng lẻ.
                </Typography>
              </Alert>
            </Box>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<ScheduleIcon />}
              onClick={() => setEditMode('time')}
            >
              Thay đổi giờ làm
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                setEditMode('shift');
                setUseCustomTime(false);
                setShiftType(existingRegistration.shiftType);
              }}
            >
              Thay đổi ca làm việc
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setEditMode('note')}
            >
              {existingRegistration.note ? 'Chỉnh sửa ghi chú' : 'Thêm ghi chú'}
            </Button>

            {/* Chức năng cho nhân viên khi cổng đăng ký mở */}
            {!isAdminView && regWindow?.isOpen && (
              <>
                {/* Nút xóa ca làm việc */}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    if (recurringInfo) {
                      // Nếu ca thuộc chu kỳ lặp, hiển thị dialog tùy chọn
                      setDeleteRecurringDialogOpen(true);
                    } else {
                      // Nếu ca không thuộc chu kỳ lặp, xóa trực tiếp
                      setDeleteShiftDialogOpen(true);
                    }
                  }}
                >
                  Xóa ca làm việc
                </Button>

                {/* Nút thêm ca làm việc bổ sung (chỉ hiển thị khi đã đăng ký cả ngày) */}
                {existingRegistration.shiftType === 'full_day' && (
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<AddIcon />}
                    onClick={() => setEditMode('additional_shift')}
                  >
                    Thêm ca làm việc
                  </Button>
                )}

              </>
            )}

            {/* Chức năng đặc biệt cho admin */}
            {isAdminView && existingRegistration && (
              <>
                {existingRegistration.status === 'cancelled' ? (
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<RestoreIcon />}
                    onClick={() => setRestoreDialogOpen(true)}
                  >
                    Khôi phục ca làm việc
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<CancelIcon />}
                      onClick={() => setCancelShiftDialogOpen(true)}
                    >
                      Hủy ca làm việc
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<EventBusyIcon />}
                      onClick={() => setEmergencyLeaveDialogOpen(true)}
                    >
                      Nghỉ đột xuất
                    </Button>
                  </>
                )}
              </>
            )}
          </Box>
        </>
      );
    }

    const currentShiftLabel = allShiftOptions.find(opt => opt.value === existingRegistration.shiftType)?.label || '';

    if (editMode === 'time') {
      return (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
            Chỉnh sửa giờ làm cho <strong>{currentShiftLabel}</strong>.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Bắt đầu" type="time" value={customStartTime} onChange={(e) => setCustomStartTime(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Kết thúc" type="time" value={customEndTime} onChange={(e) => setCustomEndTime(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
          </Grid>
        </>
      );
    }

    if (editMode === 'shift') {
      // Sửa lỗi: Chỉ hiển thị tùy chọn giờ làm sau khi đã chọn ca mới
      const hasSelectedNewShift = shiftType !== existingRegistration.shiftType;

      return (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
            Ca làm bạn đã chọn trước đây là <strong>{currentShiftLabel}</strong> ({existingRegistration.startTime} - {existingRegistration.endTime}).<br />Bạn muốn đổi sang ca nào?
          </Typography>
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <RadioGroup value={shiftType} onChange={(e) => handleShiftTypeChange(e.target.value)}>
              <Grid container spacing={1}>
                {allShiftOptions
                  .filter(option => option.value !== existingRegistration.shiftType)
                  .map(option => (
                    <Grid item xs={6} sm={4} key={option.value}>
                      <FormControlLabel
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                  ))}
              </Grid>
            </RadioGroup>
          </FormControl>

          {hasSelectedNewShift && (
            <>
              {shiftType === 'full_day' && <FormControlLabel control={<Checkbox checked={isThroughLunch} onChange={(e) => setIsThroughLunch(e.target.checked)} />} label="🍴 Đăng ký làm xuyên trưa" sx={{ display: 'block' }} />}

              <FormControlLabel control={<Checkbox checked={useCustomTime} onChange={(e) => setUseCustomTime(e.target.checked)} />} label="⏰ Tùy chỉnh thời gian cho ca mới" sx={{ display: 'block', mt: 1 }} />
              <Grid container spacing={2} sx={{ mt: 0, visibility: useCustomTime ? 'visible' : 'hidden', opacity: useCustomTime ? 1 : 0, transition: 'opacity 0.3s, visibility 0.3s' }}>
                <Grid item xs={6}><TextField fullWidth label="Bắt đầu" type="time" value={customStartTime} onChange={(e) => setCustomStartTime(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={6}><TextField fullWidth label="Kết thúc" type="time" value={customEndTime} onChange={(e) => setCustomEndTime(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
              </Grid>
            </>
          )}
        </>
      );
    }

    if (editMode === 'note') {
      return (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
            Chỉnh sửa ghi chú cho ca làm việc <strong>{currentShiftLabel}</strong>.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Ghi chú"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Nhập ghi chú cho ca làm việc này..."
            sx={{ mt: 2 }}
          />
        </>
      );
    }

    if (editMode === 'additional_shift') {
      return (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
            Bạn đã đăng ký <strong>{currentShiftLabel}</strong>. Bạn muốn thêm ca làm việc nào?
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Lưu ý:</strong> Ca làm việc mới sẽ được thêm vào chứ không thay thế ca hiện tại.
              {existingRegistration?.shiftType === 'full_day' && (
                <><br /><strong>Vì bạn đã đăng ký cả ngày (bao gồm ca sáng và ca chiều), chỉ có thể thêm ca trưa hoặc ca tối.</strong></>
              )}
            </Typography>
          </Alert>
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <RadioGroup value={shiftType} onChange={(e) => handleShiftTypeChange(e.target.value)}>
              <Grid container spacing={1}>
                {allShiftOptions
                  .filter(option => {
                    // Nếu đã đăng ký cả ngày, chỉ cho phép thêm ca trưa và ca tối
                    if (existingRegistration?.shiftType === 'full_day') {
                      return option.value === 'lunch' || option.value === 'evening';
                    }
                    // Nếu không phải cả ngày, loại bỏ "Cả ngày" vì đã có rồi
                    return option.value !== 'full_day';
                  })
                  .map(option => (
                    <Grid item xs={6} sm={4} key={option.value}>
                      <FormControlLabel
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                  ))}
              </Grid>
            </RadioGroup>
          </FormControl>

          {shiftType && (
            <>
              <FormControlLabel control={<Checkbox checked={useCustomTime} onChange={(e) => setUseCustomTime(e.target.checked)} />} label="⏰ Tùy chỉnh thời gian cho ca bổ sung" sx={{ display: 'block', mt: 1 }} />
              <Grid container spacing={2} sx={{ mt: 0, visibility: useCustomTime ? 'visible' : 'hidden', opacity: useCustomTime ? 1 : 0, transition: 'opacity 0.3s, visibility 0.3s' }}>
                <Grid item xs={6}><TextField fullWidth label="Bắt đầu" type="time" value={customStartTime} onChange={(e) => setCustomStartTime(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={6}><TextField fullWidth label="Kết thúc" type="time" value={customEndTime} onChange={(e) => setCustomEndTime(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
              </Grid>
            </>
          )}
        </>
      );
    }
    return null;
  };

  const renderNewContent = () => (
    <>
      <FormControl component="fieldset" sx={{ mt: 2 }}>
        <FormLabel component="legend">Chọn ca làm việc</FormLabel>
        <RadioGroup value={shiftType} onChange={(e) => handleShiftTypeChange(e.target.value)}>
          <Grid container spacing={1}>
            {allShiftOptions.map(option => (
              <Grid item xs={6} sm={4} key={option.value}>
                <FormControlLabel
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                  sx={{ width: '100%' }}
                />
              </Grid>
            ))}
          </Grid>
        </RadioGroup>
      </FormControl>
      {shiftType === 'full_day' && <FormControlLabel control={<Checkbox checked={isThroughLunch} onChange={(e) => setIsThroughLunch(e.target.checked)} />} label="🍴 Đăng ký làm xuyên trưa" sx={{ display: 'block' }} />}

      {/* Giờ làm việc - luôn hiển thị khi đã chọn ca */}
      {shiftType && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
          <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            ⏰ Giờ làm việc
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Giờ bắt đầu"
                type="time"
                value={customStartTime}
                onChange={(e) => {
                  setCustomStartTime(e.target.value);
                  setUseCustomTime(true);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Giờ kết thúc"
                type="time"
                value={customEndTime}
                onChange={(e) => {
                  setCustomEndTime(e.target.value);
                  setUseCustomTime(true);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
          </Grid>
          {(() => {
            const defaultTimes = getDefaultTimes(shiftType, defaultSettings);
            if (defaultTimes.start && defaultTimes.end) {
              const isModified = customStartTime !== defaultTimes.start || customEndTime !== defaultTimes.end;
              return (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">
                    Giờ mặc định: {defaultTimes.start} - {defaultTimes.end}
                    {isModified && ' (đã thay đổi)'}
                  </Typography>
                  {isModified && (
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontSize: '0.7rem', minWidth: 'auto', p: '2px 8px' }}
                      onClick={() => {
                        setCustomStartTime(defaultTimes.start);
                        setCustomEndTime(defaultTimes.end);
                        setUseCustomTime(false);
                      }}
                    >
                      Đặt lại
                    </Button>
                  )}
                </Box>
              );
            }
            return null;
          })()}
        </Box>
      )}

      {/* Chức năng chu kỳ lặp */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
        <FormControlLabel
          control={
            <Switch
              checked={isRecurring}
              onChange={(e) => setIsRecurring(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RepeatIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Đăng ký chu kỳ lặp
              </Typography>
            </Box>
          }
        />

        {isRecurring && (
          <Box sx={{ mt: 2 }}>
            {/* Chọn ngày trong tuần */}
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>Chọn ngày trong tuần</FormLabel>
              <FormGroup row>
                {[
                  { value: 0, label: 'CN' },
                  { value: 1, label: 'T2' },
                  { value: 2, label: 'T3' },
                  { value: 3, label: 'T4' },
                  { value: 4, label: 'T5' },
                  { value: 5, label: 'T6' },
                  { value: 6, label: 'T7' }
                ].map(day => (
                  <FormControlLabel
                    key={day.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={recurringDays.includes(day.value)}
                        onChange={() => handleRecurringDayChange(day.value)}
                      />
                    }
                    label={day.label}
                    sx={{ minWidth: 'auto', mr: 1 }}
                  />
                ))}
              </FormGroup>
            </FormControl>

            {/* Chọn ca làm việc cho chu kỳ lặp */}
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>
                Chọn ca làm việc {recurringShifts.length > 0 ? `(${recurringShifts.length} ca đã chọn)` : '(để trống sẽ dùng ca hiện tại)'}
              </FormLabel>
              <FormGroup row>
                {[
                  { value: 'morning', label: 'Sáng' },
                  { value: 'lunch', label: 'Trưa' },
                  { value: 'afternoon', label: 'Chiều' },
                  { value: 'evening', label: 'Tối' }
                ].map(shift => (
                  <FormControlLabel
                    key={shift.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={recurringShifts.includes(shift.value)}
                        onChange={() => handleRecurringShiftChange(shift.value)}
                      />
                    }
                    label={shift.label}
                    sx={{ minWidth: 'auto', mr: 1 }}
                  />
                ))}
              </FormGroup>
            </FormControl>

            {/* Chọn ngày kết thúc */}
            <TextField
              label="Ngày kết thúc (tùy chọn)"
              type="date"
              value={recurringEndDate}
              onChange={(e) => setRecurringEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: '100%' }}
              helperText="Để trống sẽ lặp đến hết năm"
            />
          </Box>
        )}
      </Box>

      {/* Ghi chú cho đăng ký mới */}
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Ghi chú (tùy chọn)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Nhập ghi chú cho ca làm việc này..."
        sx={{ mt: 2 }}
      />
    </>
  );

  // Hàm xử lý thay đổi ngày trong tuần
  const handleRecurringDayChange = (day) => {
    setRecurringDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  // Hàm xử lý thay đổi ca làm việc
  const handleRecurringShiftChange = (shift) => {
    setRecurringShifts(prev =>
      prev.includes(shift)
        ? prev.filter(s => s !== shift)
        : [...prev, shift]
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 0 }
      }}
    >
      <Box sx={{
        width: { xs: '100%', sm: 500 },
        maxWidth: '100%',
        maxHeight: { xs: '90vh', sm: '90vh' },
        bgcolor: 'background.paper',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: 24
      }}>
        {/* Header - Fixed */}
        <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Typography variant="h6" component="h2">{existingRegistration ? 'Chỉnh sửa' : 'Đăng ký'} lịch làm việc</Typography>
          <Typography sx={{ mt: 1 }}>Ngày: {date?.toLocaleDateString('vi-VN') || (existingRegistration && new Date(existingRegistration.date + 'T00:00:00').toLocaleDateString('vi-VN'))}</Typography>

          {/* Hiển thị tên người được chỉnh sửa */}
          {existingRegistration && (
            <Box sx={{ mt: 1, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
              <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'medium' }}>
                <strong>Nhân viên:</strong> {existingRegistration.userName || 'Không xác định'}
              </Typography>
              {(existingRegistration.department || existingRegistration.position) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {[existingRegistration.department, existingRegistration.position].filter(Boolean).join(' - ')}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Content - Scrollable */}
        <Box sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 }
        }}>
          {existingRegistration ? renderEditContent() : renderNewContent()}

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>

        {/* Footer - Fixed với các nút action */}
        {(!existingRegistration || editMode) && (
          <Box sx={{
            p: { xs: 2, sm: 3 },
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            flexShrink: 0,
            bgcolor: 'background.paper',
            flexWrap: 'wrap'
          }}>
            {existingRegistration && editMode && (
              <Button
                onClick={() => setEditMode(null)}
                sx={{ mr: 'auto' }}
                startIcon={<ArrowBackIcon />}
                size="small"
              >
                Quay lại
              </Button>
            )}
            <Button
              onClick={handleClose}
              disabled={loading}
              startIcon={<CancelIcon />}
              size="small"
            >
              Hủy
            </Button>
            <Button
              variant="contained"
              onClick={handleRegister}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              size="small"
            >
              {loading ? 'Đang xử lý...' : (
                editMode === 'additional_shift' ? 'Thêm ca làm việc' :
                  existingRegistration ? 'Lưu thay đổi' : 'Đăng ký'
              )}
            </Button>
          </Box>
        )}


        {/* Dialog xác nhận hủy ca làm việc (Admin) */}
        <Dialog open={cancelShiftDialogOpen} onClose={() => setCancelShiftDialogOpen(false)}>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CancelIcon color="warning" />
              Xác nhận hủy ca làm việc
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography>
              Bạn có chắc chắn muốn hủy ca làm việc của nhân viên <strong>{existingRegistration?.userName}</strong> không?
              Nhân viên sẽ nhận được thông báo về việc hủy ca này.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Ca làm việc: {existingRegistration?.shiftType} ({existingRegistration?.startTime} - {existingRegistration?.endTime})
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setCancelShiftDialogOpen(false)}
              startIcon={<CancelIcon />}
              disabled={cancelShiftLoading}
            >
              Hủy
            </Button>
            <Button
              onClick={handleCancelShift}
              color="warning"
              variant="contained"
              startIcon={cancelShiftLoading ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
              disabled={cancelShiftLoading}
            >
              {cancelShiftLoading ? 'Đang hủy...' : 'Hủy ca làm việc'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog khôi phục ca (Admin) */}
        <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RestoreIcon color="success" />
              Khôi phục ca làm việc
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Khôi phục ca làm việc của nhân viên <strong>{existingRegistration?.userName}</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ca làm việc: {existingRegistration?.shiftType} ({existingRegistration?.startTime} - {existingRegistration?.endTime})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ngày: {existingRegistration?.date}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setRestoreDialogOpen(false)}
              startIcon={<CancelIcon />}
              disabled={restoreLoading}
            >
              Hủy
            </Button>
            <Button
              onClick={handleRestoreShift}
              color="success"
              variant="contained"
              startIcon={restoreLoading ? <CircularProgress size={20} color="inherit" /> : <RestoreIcon />}
              disabled={restoreLoading}
            >
              {restoreLoading ? 'Đang xử lý...' : 'Khôi phục ca làm việc'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog xóa ca làm việc (Nhân viên) */}
        <Dialog open={deleteShiftDialogOpen} onClose={() => setDeleteShiftDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteIcon color="error" />
              Xác nhận xóa ca làm việc
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Bạn có chắc chắn muốn <strong>xóa hẳn</strong> ca làm việc này không?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ca làm việc: {existingRegistration?.shiftType} ({existingRegistration?.startTime} - {existingRegistration?.endTime})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ngày: {existingRegistration?.date}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Lưu ý:</strong> Ca làm việc sẽ bị xóa vĩnh viễn và không thể khôi phục.
                Bạn có thể đăng ký ca mới sau khi xóa.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteShiftDialogOpen(false)}
              startIcon={<CancelIcon />}
              disabled={deleteShiftLoading}
            >
              Hủy
            </Button>
            <Button
              onClick={handleDeleteShift}
              color="error"
              variant="contained"
              startIcon={deleteShiftLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
              disabled={deleteShiftLoading}
            >
              {deleteShiftLoading ? 'Đang xóa...' : 'Xóa ca làm việc'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog xóa ca (với tùy chọn cho chu kỳ lặp) */}
        <Dialog open={deleteRecurringDialogOpen} onClose={() => setDeleteRecurringDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteIcon color="error" />
              {recurringInfo ? 'Chọn cách xóa ca làm việc' : 'Xác nhận xóa ca làm việc'}
            </Box>
          </DialogTitle>
          <DialogContent>
            {recurringInfo ? (
              <>
                <Typography sx={{ mb: 2 }}>
                  {recurringInfo.totalShifts > 1
                    ? 'Ca này thuộc chu kỳ lặp. Bạn muốn xóa như thế nào?'
                    : 'Ca này thuộc chu kỳ lặp nhưng chỉ có 1 ca duy nhất.'
                  }
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  <strong>Thông tin chu kỳ lặp:</strong> {recurringInfo.totalShifts} ca từ {recurringInfo.firstDate} đến {recurringInfo.lastDate}
                </Typography>

                {recurringInfo.totalShifts > 1 ? (
                  <FormControl component="fieldset" sx={{ mb: 3 }}>
                    <RadioGroup
                      value={deleteOption}
                      onChange={(e) => setDeleteOption(e.target.value)}
                    >
                      <FormControlLabel
                        value="single"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              Chỉ xóa ca này ({existingRegistration?.date})
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Xóa duy nhất ca {allShiftOptions.find(opt => opt.value === existingRegistration?.shiftType)?.label || existingRegistration?.shiftType} ngày {existingRegistration?.date}
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="recurring"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              Xóa toàn bộ chu kỳ lặp ({recurringInfo.totalShifts} ca)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Xóa tất cả ca {allShiftOptions.find(opt => opt.value === recurringInfo.shiftType)?.label || recurringInfo.shiftType} từ {recurringInfo.firstDate} đến {recurringInfo.lastDate}
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                ) : (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      Chỉ xóa ca này ({existingRegistration?.date})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Xóa duy nhất ca {allShiftOptions.find(opt => opt.value === existingRegistration?.shiftType)?.label || existingRegistration?.shiftType} ngày {existingRegistration?.date}
                    </Typography>
                  </Box>
                )}

                {recurringInfo.totalShifts > 1 && deleteOption === 'single' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Lưu ý:</strong> Chỉ ca ngày {existingRegistration?.date} sẽ bị xóa. Các ca khác trong chu kỳ lặp vẫn được giữ lại.
                    </Typography>
                  </Alert>
                )}

                {recurringInfo.totalShifts > 1 && deleteOption === 'recurring' && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Cảnh báo:</strong> Tất cả {recurringInfo.totalShifts} ca trong chu kỳ lặp sẽ bị xóa vĩnh viễn và không thể khôi phục.
                    </Typography>
                  </Alert>
                )}
              </>
            ) : (
              <>
                <Typography sx={{ mb: 2 }}>
                  Bạn có chắc chắn muốn <strong>xóa hẳn</strong> ca làm việc này không?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Ca làm việc: {existingRegistration?.shiftType} ({existingRegistration?.startTime} - {existingRegistration?.endTime})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Ngày: {existingRegistration?.date}
                </Typography>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Lưu ý:</strong> Ca làm việc sẽ bị xóa vĩnh viễn và không thể khôi phục.
                    Bạn có thể đăng ký ca mới sau khi xóa.
                  </Typography>
                </Alert>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDeleteRecurringDialogOpen(false);
                setDeleteOption('single');
              }}
              startIcon={<CancelIcon />}
              disabled={deleteRecurringLoading}
            >
              Hủy
            </Button>
            <Button
              onClick={() => {
                if (deleteOption === 'single') {
                  handleDeleteShift();
                } else {
                  handleDeleteRecurring();
                }
              }}
              color="error"
              variant="contained"
              startIcon={deleteRecurringLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
              disabled={deleteRecurringLoading}
            >
              {deleteRecurringLoading ? 'Đang xóa...' : (
                recurringInfo ? (
                  recurringInfo.totalShifts > 1 ? (
                    deleteOption === 'single' ? 'Xóa ca này' : `Xóa chu kỳ lặp (${recurringInfo.totalShifts} ca)`
                  ) : 'Xóa ca này'
                ) : 'Xóa ca làm việc'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog nghỉ đột xuất (Admin) */}
        <Dialog open={emergencyLeaveDialogOpen} onClose={() => setEmergencyLeaveDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventBusyIcon color="error" />
              Đánh dấu nghỉ đột xuất
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Đánh dấu nhân viên <strong>{existingRegistration?.userName}</strong> nghỉ đột xuất cho ca làm việc này.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ca làm việc: {existingRegistration?.shiftType} ({existingRegistration?.startTime} - {existingRegistration?.endTime})
            </Typography>
            <TextField
              fullWidth
              label="Lý do nghỉ đột xuất"
              multiline
              rows={3}
              value={emergencyLeaveReason}
              onChange={(e) => setEmergencyLeaveReason(e.target.value)}
              placeholder="Nhập lý do nghỉ đột xuất (bắt buộc)"
              required
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setEmergencyLeaveDialogOpen(false);
                setEmergencyLeaveReason('');
              }}
              startIcon={<CancelIcon />}
              disabled={emergencyLeaveLoading}
            >
              Hủy
            </Button>
            <Button
              onClick={handleEmergencyLeave}
              color="error"
              variant="contained"
              startIcon={emergencyLeaveLoading ? <CircularProgress size={20} color="inherit" /> : <EventBusyIcon />}
              disabled={emergencyLeaveLoading || !emergencyLeaveReason.trim()}
            >
              {emergencyLeaveLoading ? 'Đang xử lý...' : 'Đánh dấu nghỉ đột xuất'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Modal>
  );
}
